import { useState } from 'react';
import { useParams } from 'react-router';
import { useGetSellerProduct, useUpdateCart } from '~/api';
import {
  ProductImageGallery,
  ProductHeader,
  PricingCard,
  QuantitySelector,
  ActionButtons,
  ProductSpecifications,
} from './sections';
import toast from 'react-hot-toast';
import { BackButton } from '~/components/ui';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '~/utils';
import type { SellerProductQuery } from '~/graphql/graphql';

export function ProductView() {
  const { productId } = useParams();
  const { data, isLoading } = useGetSellerProduct(productId!);
  const [quantity, setQuantity] = useState('0');
  const { mutate: updateCart, isPending } = useUpdateCart();
  const { t } = useTranslation('product');

  if (isLoading) {
    return <div>Loading...</div>;
  }
  const sellerProduct = data?.sellerProduct;

  if (!sellerProduct) {
    return <div>{t('product.not_found')}</div>;
  }

  const product = sellerProduct.platformProductLength.platformProduct;

  const totalPrice = (sellerProduct.pricePerUnit || 0) * Number(quantity);

  const handleAddToCart = () => {
    updateCart(
      {
        sellingOrganizationId: sellerProduct.organization.id,
        productId: sellerProduct.platformProductLength.id!,
        quantity: { value: Number(quantity), unit: 'MBF' },
      },
      {
        onSuccess: () => toast.success(t('product.add_to_cart_success')),
        onError: (error) =>
          toast.error(
            t('product.add_to_cart_error', { message: error.message })
          ),
      }
    );
  };

  const pageTitle = product.name
    ? `${product.name} - ${t('title')}`
    : t('product.title');

  return (
    <div className="space-y-6">
      <title>{pageTitle}</title>
      <BackButton
        link="/explore-products"
        label={t('product.back_button_label')}
      />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <ProductImageGallery
          mainImage="🌲"
          thumbnails={['🌲', '🌲', '🌲', '🌲']}
        />

        <div className="space-y-6">
          <ProductHeader
            name={product.name || ''}
            seller={sellerProduct.organization.name || ''}
            code={
              sellerProduct.platformProductLength.platformProduct.code || ''
            }
          />

          <div className="flex items-center gap-2" />

          <PricingCard
            price={sellerProduct.pricePerUnit}
            unit={'TBD'}
            quantityAvailable={1000}
          />

          <QuantitySelector
            sellerProduct={sellerProduct}
            quantity={quantity}
            onQuantityChange={setQuantity}
          />

          <ActionButtons
            quantity={Number(quantity)}
            totalPrice={totalPrice}
            unit="MBF"
            onAddToCart={handleAddToCart}
            loading={isPending}
          />
        </div>
      </div>

      <ProductSpecifications
        specifications={getProductFullSpecifications(sellerProduct)}
      />
    </div>
  );
}

function getProductFullSpecifications(
  sellerProduct: NonNullable<SellerProductQuery['sellerProduct']>
): Record<string, string> {
  const length = sellerProduct.platformProductLength.length;
  const product = sellerProduct.platformProductLength.platformProduct;

  const specs: Record<string, string> = {};
  if (product.code != null) {
    specs['product_code'] = product.code;
  }

  specs['name'] = product.name ?? '';
  const properties = product.properties || {};
  if (properties.species?.code) {
    specs['species'] = properties.species.code;
    specs['species_name'] = properties.species.name || '';
  }
  if (properties.grade?.code) {
    specs['grade'] = properties.grade.code;
    specs['grade_name'] = properties.grade.name || '';
  }
  if (properties.dryness?.code) {
    specs['dryness'] = properties.dryness.code;
    specs['dryness_name'] = properties.dryness.name || '';
  }
  if (properties.treatment?.code) {
    specs['treatment'] = properties.treatment.code;
    specs['treatment_name'] = properties.treatment.name || '';
  }
  if (properties.construction?.code) {
    specs['construction'] = properties.construction.code;
    specs['construction_name'] = properties.construction.name || '';
  }

  specs['nominal_thickness'] = product.nominalThicknessInches?.toString() || '';
  specs['actual_thickness'] = product.actualThicknessInches?.toString() || '';
  specs['nominal_width'] = product.nominalWidthInches?.toString() || '';
  specs['actual_width'] = product.actualWidthInches?.toString() || '';
  specs['actual_length'] = length.actualLengthInches.toString();

  specs['price'] = formatCurrency(sellerProduct.pricePerUnit) + '/TBD';
  specs['length'] = length.displayValue;
  specs['minimum_order_quantity'] =
    sellerProduct.minimumOrderQuantity.toString();
  specs['ready_to_ship_days'] = sellerProduct.readyToShipDays.toString();

  return specs;
}
